import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { BadgeComponent } from '../../../shared/components/ui/badge/badge.component';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { AuthService } from '../../auth-pages/services/auth.service';
import { AuthApiService } from '../../auth-pages/services/auth-api.service';
import { ChatflowDetailModalComponent } from '../chatflow-detail-modal/chatflow-detail-modal.component';
import { ChatflowApiService } from '../services/chatflow-api.service';
import { extractApiErrorMessage } from '../services/chatflow-error.util';
import { ChatflowTemplateApiService } from '../services/chatflow-template-api.service';
import {
  ChatflowFlowValidationResponse,
  ChatflowResponse,
  ChatflowStatus,
  ChatflowValidationStatus,
  ChatflowTemplateResponse,
  ChatflowType
} from '../models/chatflow.model';
import { ToastService } from '../../../shared/services/toast.service';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';
import {
  CHATFLOW_TEMPLATE_CATEGORIES,
  ChatflowCategoryOption,
  getChatflowCategoryLabel,
  getChatflowPriorityLabel
} from '../services/chatflow-label.util';

type BadgeColor = 'primary' | 'success' | 'error' | 'warning' | 'info' | 'light' | 'dark';

@Component({
  selector: 'app-chatflow-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    PageBreadcrumbComponent,
    BadgeComponent,
    ModalComponent,
    HasPermissionDirective,
    ChatflowDetailModalComponent
  ],
  templateUrl: './chatflow-list.component.html'
})
export class ChatflowListComponent implements OnInit, OnDestroy {
  readonly estados: ChatflowStatus[] = ['DRAFT', 'ACTIVE', 'ARCHIVED'];
  readonly tipos: ChatflowType[] = ['LIVE_CHAT', 'BOT', 'AI_CHATBOT'];
  readonly categoriasPlantilla: ChatflowCategoryOption[] = CHATFLOW_TEMPLATE_CATEGORIES;

  chatflows: ChatflowResponse[] = [];
  templates: ChatflowTemplateResponse[] = [];
  loading = false;
  error = '';
  templateError = '';
  actionError = '';
  actionMessage = '';

  filtroEstado = '';
  filtroTipo = '';
  filtroBusqueda = '';

  /** Sección activa: false = chatflows en uso (activos + borradores), true = archivados. */
  vistaArchivados = false;
  /** Estados que ofrece el dropdown dentro de la vista "Activos" (ARCHIVED tiene su propia sección). */
  readonly estadosActivos: ChatflowStatus[] = ['DRAFT', 'ACTIVE'];

  /**
   * Lista que se pinta según la sección elegida. Los archivados NO se mezclan con los activos:
   * viven en su propia sección (INC_CRM_019 #20). El backend devuelve todos los estados, así que
   * la separación se hace aquí (la lista no está paginada).
   */
  get chatflowsVisibles(): ChatflowResponse[] {
    return this.chatflows.filter((c) => {
      const archivado = c.status === 'ARCHIVED';
      if (this.vistaArchivados) return archivado;
      if (archivado) return false;
      return !this.filtroEstado || c.status === this.filtroEstado;
    });
  }

  get countActivos(): number {
    return this.chatflows.filter((c) => c.status !== 'ARCHIVED').length;
  }

  get countArchivados(): number {
    return this.chatflows.filter((c) => c.status === 'ARCHIVED').length;
  }

  /** Cambia entre la sección "Activos" y "Archivados". El sub-filtro de estado no aplica a archivados. */
  verArchivados(archivados: boolean): void {
    this.vistaArchivados = archivados;
    if (archivados) {
      this.filtroEstado = '';
    }
  }

  validationLoadingId: number | null = null;
  expandedErrorId: number | null = null;
  openMenuId: number | null = null;

  // Modal "Guardar como plantilla"
  showTemplateModal = false;
  templateTarget: ChatflowResponse | null = null;
  tplNombre = '';
  tplDescripcion = '';
  tplCategoria = '';
  savingTemplate = false;
  templateModalError = '';

  // Modal "Duplicar chatflow" (reemplaza window.prompt — INC_CRM_019 #20)
  showDuplicateModal = false;
  duplicateTarget: ChatflowResponse | null = null;
  duplicateNombre = '';
  duplicatingChatflow = false;
  duplicateModalError = '';

  // Modal "Ver detalle" (INC_CRM_019 #22) — mismo patrón que chatflow-templates.
  showDetailModal = false;
  selectedChatflow: ChatflowResponse | null = null;
  /** Nombre del creador que se muestra en la cabecera del detalle. */
  detailOwnerName = '';

  private readonly searchSubject = new Subject<void>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private chatflowApi: ChatflowApiService,
    private templateApi: ChatflowTemplateApiService,
    private authApiService: AuthApiService,
    private router: Router,
    private toast: ToastService,
    private confirmDialog: ConfirmDialogService
  ) {}

  ngOnInit(): void {
    this.searchSubject
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(() => this.cargarChatflows());

    this.cargarTemplates();
    this.cargarChatflows();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Búsqueda server-side con debounce al escribir en el input. */
  onBusquedaChange(): void {
    this.searchSubject.next();
  }

  @HostListener('document:click')
  cerrarMenus(): void {
    this.openMenuId = null;
  }

  toggleMenu(chatflow: ChatflowResponse, event: Event): void {
    event.stopPropagation();
    this.openMenuId = this.openMenuId === chatflow.id ? null : chatflow.id;
  }

  copiarId(chatflow: ChatflowResponse): void {
    navigator.clipboard.writeText(String(chatflow.id)).then(
      () => this.toast.info(`ID ${chatflow.id} copiado al portapapeles.`),
      () => this.toast.error({ title: 'Error', message: 'No se pudo copiar el ID al portapapeles.' })
    );
  }

  /** Ver detalle (INC_CRM_019 #22): el id numérico interno y la descripción larga viven aquí, no en la fila de la lista. */
  verDetalle(chatflow: ChatflowResponse): void {
    this.openMenuId = null;
    this.selectedChatflow = chatflow;
    this.detailOwnerName = '';
    this.showDetailModal = true;
    this.resolverResponsable(chatflow);
  }

  cerrarDetalle(): void {
    this.showDetailModal = false;
    this.selectedChatflow = null;
    this.detailOwnerName = '';
  }

  /** Salto directo desde el detalle a la pestaña correspondiente del editor. */
  abrirEditorDesdeDetalle(tab: 'general' | 'flujo' | 'analitica'): void {
    const chatflow = this.selectedChatflow;
    this.cerrarDetalle();
    if (chatflow) {
      this.router.navigate(['/chatflows', chatflow.id, 'editor'], { queryParams: { tab } });
    }
  }

  /**
   * Nombre del creador para la cabecera del detalle. Se resuelve aquí y se pasa al modal;
   * si el id es el del usuario en sesión se evita la llamada. Un fallo deja el campo vacío:
   * el detalle no debe romperse por no poder resolver un nombre.
   */
  private resolverResponsable(chatflow: ChatflowResponse): void {
    const ownerId = chatflow.createdByUserId;
    if (!ownerId) return;

    const currentUser = this.authService.getCurrentUser();
    if (currentUser?.id === ownerId) {
      this.detailOwnerName = `${currentUser.fullname ?? ''} ${currentUser.lastname ?? ''}`.trim();
      return;
    }
    this.authApiService.getUserById(ownerId).subscribe({
      next: (user) => this.detailOwnerName = `${user.fullname ?? ''} ${user.lastname ?? ''}`.trim(),
      error: () => this.detailOwnerName = ''
    });
  }

  cargarTemplates(): void {
    this.templateError = '';
    this.templateApi.getTemplates().subscribe({
      next: (data) => {
        this.templates = [...data].sort((a, b) => (a.sortOrder || 9999) - (b.sortOrder || 9999));
      },
      error: (err) => {
        this.templates = [];
        this.templateError = extractApiErrorMessage(err, 'No se pudo cargar la biblioteca de plantillas');
      }
    });
  }

  cargarChatflows(): void {
    const businessId = this.authService.getBusinessId() || 1;
    this.loading = true;
    this.error = '';
    this.actionError = '';

    // No enviamos `status` al backend: cargamos todos los estados y separamos activos/archivados
    // en el cliente (ver `chatflowsVisibles`), para que ambas secciones tengan conteos estables.
    this.chatflowApi.getChatflows({
      businessId,
      type: this.filtroTipo ? this.filtroTipo as ChatflowType : undefined,
      search: this.filtroBusqueda.trim() || undefined
    }).subscribe({
      next: (data) => {
        this.chatflows = [...data].sort((a, b) => a.priority - b.priority);
        this.loading = false;
      },
      error: (err) => {
        this.error = extractApiErrorMessage(err, 'No se pudo cargar los chatflows');
        this.loading = false;
      }
    });
  }

  buscar(): void {
    this.cargarChatflows();
  }

  limpiarFiltros(): void {
    this.filtroEstado = '';
    this.filtroTipo = '';
    this.filtroBusqueda = '';
    this.vistaArchivados = false;
    this.cargarChatflows();
  }

  nuevoChatflow(): void {
    this.router.navigate(['/chatflows/crear']);
  }

  /**
   * Acción ÚNICA de edición: abre el editor con pestañas, empezando por "Datos generales"
   * (nombre, descripción, tipo, prioridad), que antes vivían en el formulario suelto.
   *
   * Sustituye a las cuatro acciones que la lista ofrecía —"Diseñar flujo", "Reglas del
   * widget", "Configurar" y este "Editar"— y que para el usuario terminaban en la misma
   * pantalla, solo que en pestañas distintas (INC_CRM_019 (3) inc19).
   */
  editarChatflow(chatflow: ChatflowResponse): void {
    this.router.navigate(['/chatflows', chatflow.id, 'editor'], { queryParams: { tab: 'general' } });
  }

  /** Atajo desde el modal de detalle: entra directo a la pestaña del constructor. */
  abrirBuilder(chatflow: ChatflowResponse): void {
    this.router.navigate(['/chatflows', chatflow.id, 'editor'], { queryParams: { tab: 'flujo' } });
  }

  /** Abre el modal para pedir el nombre de la copia (reemplaza `window.prompt`). */
  abrirDuplicar(chatflow: ChatflowResponse): void {
    this.openMenuId = null;
    this.duplicateTarget = chatflow;
    this.duplicateNombre = `${chatflow.name} - copia`;
    this.duplicateModalError = '';
    this.duplicatingChatflow = false;
    this.showDuplicateModal = true;
  }

  cerrarModalDuplicar(): void {
    if (this.duplicatingChatflow) return;
    this.showDuplicateModal = false;
    this.duplicateTarget = null;
  }

  confirmarDuplicar(): void {
    if (!this.duplicateTarget || this.duplicatingChatflow) return;

    const nombre = this.duplicateNombre.trim();
    if (!nombre) {
      this.duplicateModalError = 'El nombre de la copia es obligatorio.';
      return;
    }

    const original = this.duplicateTarget;
    this.duplicatingChatflow = true;
    this.duplicateModalError = '';
    this.actionError = '';
    this.actionMessage = '';

    this.chatflowApi.duplicateChatflow(original.id, { name: nombre }).subscribe({
      next: (nuevo) => {
        this.duplicatingChatflow = false;
        this.showDuplicateModal = false;
        this.duplicateTarget = null;
        this.actionMessage = `Se duplicó "${original.name}" como "${nuevo.name}".`;
        this.toast.created('Chatflow', this.actionMessage);
        this.cargarChatflows();
      },
      error: (err) => {
        const msg = extractApiErrorMessage(err, 'No se pudo duplicar el chatflow');
        this.duplicatingChatflow = false;
        this.duplicateModalError = msg;
        this.toast.error({ title: 'Error', message: msg });
      }
    });
  }

  async eliminarChatflow(chatflow: ChatflowResponse): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar chatflow',
      message: `¿Está seguro de eliminar el chatflow "${chatflow.name}"? Esta acción es irreversible: se borrará el flujo completo con sus nodos, reglas y configuración.`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar'
    });
    if (!ok) return;

    this.actionError = '';
    this.actionMessage = '';

    this.chatflowApi.deleteChatflow(chatflow.id).subscribe({
      next: () => {
        this.toast.deleted('Chatflow');
        this.cargarChatflows();
      },
      error: (err) => {
        // 409: tiene conversaciones registradas (el backend sugiere archivar); 404: no existe.
        const msg = err?.status === 409
          ? 'No se puede eliminar: tiene conversaciones registradas. Archívalo en su lugar.'
          : extractApiErrorMessage(err, 'No se pudo eliminar el chatflow.');
        this.actionError = msg;
        this.toast.error({ title: 'Error', message: msg });
      }
    });
  }

  abrirGuardarPlantilla(chatflow: ChatflowResponse): void {
    this.openMenuId = null;
    this.templateTarget = chatflow;
    this.tplNombre = chatflow.name;
    this.tplDescripcion = chatflow.description || '';
    this.tplCategoria = '';
    this.templateModalError = '';
    this.savingTemplate = false;
    this.showTemplateModal = true;
  }

  cerrarModalPlantilla(): void {
    if (this.savingTemplate) return;
    this.showTemplateModal = false;
    this.templateTarget = null;
  }

  guardarComoPlantilla(): void {
    if (!this.templateTarget || this.savingTemplate) return;

    const nombre = this.tplNombre.trim();
    if (!nombre) {
      this.templateModalError = 'El nombre de la plantilla es obligatorio.';
      return;
    }

    this.savingTemplate = true;
    this.templateModalError = '';

    this.chatflowApi.saveAsTemplate(this.templateTarget.id, {
      name: nombre,
      description: this.tplDescripcion.trim() || undefined,
      category: this.tplCategoria || undefined
    }).subscribe({
      next: () => {
        this.savingTemplate = false;
        this.showTemplateModal = false;
        this.templateTarget = null;
        this.toast.created('Plantilla');
        this.cargarTemplates();
      },
      error: (err) => {
        // 400: el chatflow no tiene nodos configurados / categoría inválida.
        const msg = extractApiErrorMessage(err, 'No se pudo guardar la plantilla.');
        this.savingTemplate = false;
        this.templateModalError = msg;
        this.toast.error({ title: 'Error', message: msg });
      }
    });
  }

  activarChatflow(chatflow: ChatflowResponse): void {
    // RF-01.03: AI_CHATBOT bloqueado hasta que el backend lo soporte
    if (chatflow.chatflowType === 'AI_CHATBOT') {
      this.actionError = 'AI Chatbot todavía no está disponible en esta versión. Próximamente.';
      return;
    }
    // Solo se bloquea lo que ya se sabe roto. "Pendiente" significa "sin validar todavía",
    // no "inválido": el backend valida al activar y responde con los errores concretos si
    // los hay, así que exigir un "Validar" manual previo sería un paso de más.
    if (chatflow.validationStatus === 'INVALID') {
      this.actionError = 'El chatflow no puede activarse porque su configuración tiene errores. Ejecuta "Validar" para ver cuáles.';
      return;
    }

    this.actionError = '';
    this.actionMessage = '';
    this.validationLoadingId = chatflow.id;

    this.chatflowApi.updateStatus(chatflow.id, 'ACTIVE').subscribe({
      next: () => {
        this.actionMessage = `El chatflow "${chatflow.name}" quedó activo.`;
        this.validationLoadingId = null;
        this.toast.updated('Chatflow');
        this.cargarChatflows();
      },
      error: (err) => {
        const msg = extractApiErrorMessage(err, 'No se pudo activar el chatflow');
        // Mensaje amigable si el backend bloquea AI_CHATBOT
        this.actionError = msg.toLowerCase().includes('ai') || msg.toLowerCase().includes('chatbot')
          ? 'AI Chatbot todavía no está disponible en esta versión. Próximamente.'
          : msg;
        this.validationLoadingId = null;
        this.toast.error({ title: 'Error', message: this.actionError });
      }
    });
  }

  /** Devuelve true si este tipo de chatflow está actualmente bloqueado para activación */
  isActivationBlocked(chatflow: ChatflowResponse): boolean {
    return chatflow.chatflowType === 'AI_CHATBOT';
  }

  async actualizarEstado(chatflow: ChatflowResponse, status: ChatflowStatus): Promise<void> {
    if (status === 'ACTIVE') {
      this.activarChatflow(chatflow);
      return;
    }

    if (status === 'ARCHIVED') {
      const ok = await this.confirmDialog.confirmDanger({
        title: 'Archivar chatflow',
        message: `¿Deseas archivar "${chatflow.name}"? Dejará de estar disponible para el widget hasta que lo actives de nuevo. Podrás encontrarlo en la sección "Archivados".`,
        confirmText: 'Archivar',
        cancelText: 'Cancelar'
      });
      if (!ok) return;
    }

    this.actionError = '';
    this.actionMessage = '';

    this.chatflowApi.updateStatus(chatflow.id, status).subscribe({
      next: () => {
        this.actionMessage =
          status === 'ARCHIVED'
            ? `El chatflow "${chatflow.name}" fue archivado.`
            : `El chatflow "${chatflow.name}" volvió a borrador.`;
        this.toast.updated('Chatflow');
        this.cargarChatflows();
      },
      error: (err) => {
        this.actionError = extractApiErrorMessage(err, 'No se pudo actualizar el estado');
        this.toast.error({ title: 'Error', message: this.actionError });
      }
    });
  }

  alternarValidacion(chatflow: ChatflowResponse): void {
    if (this.expandedErrorId === chatflow.id) {
      this.expandedErrorId = null;
      return;
    }

    this.validationLoadingId = chatflow.id;
    this.chatflowApi.validatePublish(chatflow.id).subscribe({
      next: () => {
        this.validationLoadingId = null;
        this.cargarChatflows();
        if (chatflow.validationStatus === 'INVALID') {
          this.expandedErrorId = chatflow.id;
        }
      },
      error: (err) => {
        const msg = extractApiErrorMessage(err, 'No se pudo validar el chatflow');
        this.actionError = msg;
        this.toast.error({ title: 'Error', message: msg });
        this.validationLoadingId = null;
      }
    });
  }

  toggleErrores(chatflow: ChatflowResponse): void {
    if (this.expandedErrorId === chatflow.id) {
      this.expandedErrorId = null;
    } else {
      this.expandedErrorId = chatflow.id;
    }
  }

  getStatusColor(status: ChatflowStatus): BadgeColor {
    if (status === 'ACTIVE') return 'success';
    if (status === 'ARCHIVED') return 'dark';
    return 'warning';
  }

  getStatusLabel(status: ChatflowStatus): string {
    if (status === 'ACTIVE') return 'Activo';
    if (status === 'ARCHIVED') return 'Archivado';
    return 'Borrador';
  }

  getTypeLabel(type: ChatflowType): string {
    if (type === 'LIVE_CHAT') return 'Chat en vivo';
    if (type === 'AI_CHATBOT') return 'Chatbot IA';
    return 'Bot';
  }

  getTemplateName(templateId?: number): string {
    if (!templateId) return 'Sin plantilla';
    return this.templates.find((template) => template.id === templateId)?.name || `Plantilla #${templateId}`;
  }

  getTemplateCategory(templateId?: number): string {
    if (!templateId) return '';
    return this.templates.find((template) => template.id === templateId)?.category || '';
  }

  getCategoryLabel(category?: string): string {
    return getChatflowCategoryLabel(category);
  }

  getPriorityLabel(priority: number): string {
    return getChatflowPriorityLabel(priority);
  }

  getPriorityBadgeClass(priority: number): string {
    const label = getChatflowPriorityLabel(priority);
    if (label === 'Crítica') {
      return 'bg-error-50 text-error-700 ring-1 ring-inset ring-error-500/20 dark:bg-error-500/10 dark:text-error-400';
    }
    if (label === 'Alta') {
      return 'bg-orange-50 text-orange-600 ring-1 ring-inset ring-orange-500/20 dark:bg-orange-500/10 dark:text-orange-400';
    }
    if (label === 'Media') {
      return 'bg-warning-50 text-warning-700 ring-1 ring-inset ring-warning-500/20 dark:bg-warning-500/10 dark:text-warning-400';
    }
    return 'bg-gray-100 text-gray-600 ring-1 ring-inset ring-gray-400/20 dark:bg-gray-800 dark:text-gray-300';
  }

  getValidationColor(validationStatus: ChatflowValidationStatus | undefined): BadgeColor {
    if (validationStatus === 'VALID') return 'success';
    if (validationStatus === 'INVALID') return 'error';
    if (validationStatus === 'PENDING') return 'warning';
    return 'light';
  }

  getValidationLabel(validationStatus: ChatflowValidationStatus | undefined): string {
    if (validationStatus === 'VALID') return 'Válido';
    if (validationStatus === 'INVALID') return 'Inválido';
    if (validationStatus === 'PENDING') return 'Pendiente';
    return 'Desconocido';
  }

  get totalActive(): number {
    return this.chatflows.filter((chatflow) => chatflow.status === 'ACTIVE').length;
  }

  get totalDraft(): number {
    return this.chatflows.filter((chatflow) => chatflow.status === 'DRAFT').length;
  }

  get totalReadyToPublish(): number {
    return this.chatflows.filter((chatflow) => chatflow.validationStatus === 'VALID').length;
  }

  get totalBots(): number {
    return this.chatflows.filter((chatflow) => chatflow.chatflowType !== 'LIVE_CHAT').length;
  }
}
